import * as React from 'react';
import propz from 'propz';
import { parse } from 'query-string';
import { History, Location } from 'history';
import { AppUser } from 'Src/views/App/App';
import { Tournament } from 'Src/models/tournament';
import { SchoolEvent } from 'Src/models/event';
import { Sport } from 'Src/models/sport';
import { Loader } from 'Src/components/Loader/Loader';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { UnsavedChangesModal } from 'Src/components/UnsavedChangesModal/UnsavedChangesModal';
import { SearchMultiSelectFilter } from 'Src/components/SearchMultiSelectFilter/SearchMultiSelectFilter';
import { RichMatchResultEditor } from 'Src/components/RichMatchResultEditor/RichMatchResultEditor';
import { getTournament, getTournamentCompetitionRuntime } from 'Src/helpers/service/adminSU/tournamentsTable';
import { getSchoolEvent } from 'Src/helpers/service/admin/event';
import {
  assignPitchByTournamentIdAndEventId,
  deleteEvent,
  getTournamentEvents,
  getTournamentEventsCount,
  getTournamentEventsBulkManagement,
  getTournamentEventIds,
  getTournamentEventSports,
  unassignPitchByTournamentIdAndEventId,
  updateTournamentEventsBulk
} from 'Src/helpers/service/adminSU/tournamentEventsTable';
import { getEventStatus } from 'Src/helpers/accessor/accessor';
import { EVENT_STATUS_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/event';
import { TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/tournament';
import { formatTournamentAges } from 'Src/helpers/ageGroups';
import { isMiniTeamTournament, isTeamTournament } from 'Src/helpers/tournament/tournament';
import './TournamentEvents.scss';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

interface EventDraft {
  event: SchoolEvent;
  shortDescription?: string;
  startTime?: string;
  endTime?: string;
  tournamentGroupId?: string;
  pitchId?: string | null;
  score1?: string;
  score2?: string;
}

interface State {
  tournament?: Tournament;
  items: SchoolEvent[];
  total: number;
  page: number;
  isLoading: boolean;
  isSaving: boolean;
  saveError: string;
  isInlineMode: boolean;
  drafts: { [eventId: string]: EventDraft };
  selectedIds: string[];
  selectedEvents: { [eventId: string]: SchoolEvent };
  sports: Sport[];
  eventAgeOptions: number[];
  eventGenderOptions: string[];
  sportFilters: string[];
  ageFilters: string[];
  genderFilters: string[];
  groupFilters: string[];
  pitchFilters: string[];
  stageFilters: string[];
  statusFilters: string[];
  categoryFilters: string[];
  classificationFilters: string[];
  roundFilters: string[];
  sportFilterDrafts: string[];
  ageFilterDrafts: string[];
  genderFilterDrafts: string[];
  groupFilterDrafts: string[];
  pitchFilterDrafts: string[];
  stageFilterDrafts: string[];
  statusFilterDrafts: string[];
  categoryFilterDrafts: string[];
  classificationFilterDrafts: string[];
  roundFilterDrafts: string[];
  isFiltersOpen: boolean;
  isColumnsOpen: boolean;
  hiddenColumns: string[];
  isDiscardOpen: boolean;
  isAssignPitchOpen: boolean;
  selectedPitchId: string;
  selectedFormatCategory: string;
  selectedFormatClassification: string;
  selectedFormatStage: string;
  isApplyingFormat: boolean;
  isDeletingOpen: boolean;
  resultEvent?: SchoolEvent;
  isResultLoading: boolean;
  resultError: string;
  structureNotice?: 'READY' | 'REQUIRES_DECISION' | 'STALE';
  statusCounts: {
    live: number;
    upcoming: number;
    finished: number;
    onHold: number;
    cancelled: number;
  };
}

const COLUMNS = [
  { key: 'number', label: 'Event number' },
  { key: 'sport', label: 'Sport' },
  { key: 'age', label: 'Age' },
  { key: 'gender', label: 'Gender' },
  { key: 'eventCategory', label: 'Category' },
  { key: 'classification', label: 'Classification' },
  { key: 'formatRound', label: 'Round' },
  { key: 'date', label: 'Date' },
  { key: 'startTime', label: 'Start time' },
  { key: 'stage', label: 'Stage' },
  { key: 'group', label: 'Group' },
  { key: 'pitch', label: 'Pitch' },
  { key: 'fixture', label: 'Event', required: true },
  { key: 'description', label: 'Short description' },
  { key: 'score', label: 'Result' },
  { key: 'status', label: 'Status' }
];

const Icon = ({ path }: { path: string }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
    <path d={path} />
  </svg>
);

const storageKey = (userId: string) => `columnVisibility__${userId || 'anonymous'}__tournamentBulkEvents`;
const loadHiddenColumns = (userId: string): string[] => {
  try {
    const value = JSON.parse(localStorage.getItem(storageKey(userId)) || '[]');
    return Array.isArray(value) ? value.filter(key => key !== 'fixture') : [];
  } catch {
    return [];
  }
};

const toggleValue = (values: string[], value: string) =>
  values.includes(value) ? values.filter(item => item !== value) : [...values, value];

const EVENT_STATUS_OPTIONS = [
  { id: 'NOT_STARTED', name: 'Not started' },
  ...Object.keys(EVENT_STATUS_SERVER_TO_CLIENT_MAPPING).map(id => ({
    id,
    name: EVENT_STATUS_SERVER_TO_CLIENT_MAPPING[id]
  }))
];

const EVENT_GENDER_OPTIONS = Object.keys(TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING).map(id => ({
  id,
  name: TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[id]
}));

const EVENT_CATEGORY_OPTIONS = ['TRACK', 'FIELD', 'POOL', 'COURSE', 'COURT', 'OTHER'].map(id => ({
  id,
  name: id.charAt(0) + id.slice(1).toLowerCase()
}));
const EVENT_CLASSIFICATION_OPTIONS = [
  { id: 'OPEN', name: 'Open' },
  { id: 'PARA', name: 'Para' }
];
const EVENT_ROUND_OPTIONS = ['STRAIGHT_FINAL', 'HEAT', 'QUALIFIER', 'SEMI_FINAL', 'FINAL'].map(id => ({
  id,
  name: id
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/(^|\s)\S/g, value => value.toUpperCase())
}));

const GROUP_STAGE_FILTER = 'GROUP_STAGE';
const KNOCKOUT_ROUND_OPTIONS = [
  { id: 'ROUND_OF_32', name: 'Round of 32' },
  { id: 'ROUND_OF_16', name: 'Round of 16' },
  { id: 'ROUND_OF_8', name: 'Round of 8' },
  { id: 'QUARTER_FINAL', name: 'Quarter-final' },
  { id: 'SEMI_FINAL', name: 'Semi-final' },
  { id: 'FINAL', name: 'Final' },
  { id: 'PLAYOFF', name: 'Play-off / placing' }
];

type AppliedFilterKey =
  | 'sportFilters'
  | 'ageFilters'
  | 'genderFilters'
  | 'groupFilters'
  | 'pitchFilters'
  | 'stageFilters'
  | 'statusFilters'
  | 'categoryFilters'
  | 'classificationFilters'
  | 'roundFilters';

const pad = (value: number) => (value < 10 ? `0${value}` : String(value));
const toDatetimeLocal = (value: string) => {
  const date = new Date(value);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}`;
};

export class SchoolUnionTournamentEvents extends React.Component<Props, State> {
  state: State = {
    items: [],
    total: 0,
    page: 1,
    isLoading: true,
    isSaving: false,
    saveError: '',
    isInlineMode: false,
    drafts: {},
    selectedIds: [],
    selectedEvents: {},
    sports: [],
    eventAgeOptions: [],
    eventGenderOptions: [],
    sportFilters: [],
    ageFilters: [],
    genderFilters: [],
    groupFilters: [],
    pitchFilters: [],
    stageFilters: [],
    statusFilters: [],
    categoryFilters: [],
    classificationFilters: [],
    roundFilters: [],
    sportFilterDrafts: [],
    ageFilterDrafts: [],
    genderFilterDrafts: [],
    groupFilterDrafts: [],
    pitchFilterDrafts: [],
    stageFilterDrafts: [],
    statusFilterDrafts: [],
    categoryFilterDrafts: [],
    classificationFilterDrafts: [],
    roundFilterDrafts: [],
    isFiltersOpen: false,
    isColumnsOpen: false,
    hiddenColumns: loadHiddenColumns(this.props.user.userId),
    isDiscardOpen: false,
    isAssignPitchOpen: false,
    selectedPitchId: '',
    selectedFormatCategory: '',
    selectedFormatClassification: '',
    selectedFormatStage: '',
    isApplyingFormat: false,
    isDeletingOpen: false,
    resultEvent: undefined,
    isResultLoading: false,
    resultError: '',
    statusCounts: { live: 0, upcoming: 0, finished: 0, onHold: 0, cancelled: 0 }
  };

  componentDidMount() {
    const tournamentId = this.getTournamentId();
    Promise.all([
      getTournament(this.props.user, tournamentId),
      getTournamentEventSports(this.props.user, tournamentId).catch(() => [] as Sport[])
    ]).then(([tournament, sports]) =>
      this.setState({ tournament, sports }, () => {
        window.dispatchEvent(
          new CustomEvent('sit:topbar-breadcrumb', {
            detail: `${tournament.name} / Bulk event management`
          })
        );
        this.load(1);
        this.refreshStructureNotice();
      })
    );
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
  }

  handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!Object.keys(this.state.drafts).length) return;
    event.preventDefault();
    event.returnValue = '';
  };

  getTournamentId = () => String(parse(this.props.history.location.search).tournament || '');

  getCompetitionDefinition = () =>
    this.state.tournament?.competitionStructure?.published || this.state.tournament?.competitionStructure?.draft;

  getStructureMatchRound = (match: any): string => {
    if (match.round) return String(match.round);
    const tournament = this.state.tournament;
    const group = (tournament?.customGroups || []).find(item => String(item.id) === String(match.groupId));
    const stage = (tournament?.tournamentLayout || []).find(item => String(item.id) === String(group?.stageId));
    return ({ QUALIFIER: 'QUARTER_FINAL', PLAYOFF: 'SEMI_FINAL', FINAL: 'FINAL' } as any)[String(stage?.type)] || 'PLAYOFF';
  };

  getStageFilterOptions = () => {
    const tournament = this.state.tournament;
    const usedRounds = new Set(
      (this.getCompetitionDefinition()?.matches || []).map(match => this.getStructureMatchRound(match))
    );
    const hasGroupStage = (tournament?.tournamentLayout || []).some(stage => stage.type === 'GROUP');
    return [
      ...(hasGroupStage ? [{ id: GROUP_STAGE_FILTER, name: 'Group stage' }] : []),
      ...KNOCKOUT_ROUND_OPTIONS.filter(round => usedRounds.has(round.id))
    ];
  };

  getEventKnockoutRound = (event: SchoolEvent): string => {
    const match = (this.getCompetitionDefinition()?.matches || []).find(
      item =>
        String(item.eventId || '') === String(event.id) ||
        (event.tournamentStructureMatchId && String(item.id) === String(event.tournamentStructureMatchId))
    );
    if (!match) return '';
    const round = this.getStructureMatchRound(match);
    return KNOCKOUT_ROUND_OPTIONS.find(item => item.id === round)?.name || round.replace(/_/g, ' ');
  };

  getWhere = () => {
    const where: any = {};
    const {
      tournament,
      sportFilters,
      ageFilters,
      genderFilters,
      groupFilters,
      stageFilters,
      pitchFilters,
      statusFilters,
      categoryFilters,
      classificationFilters,
      roundFilters
    } = this.state;
    if (sportFilters.length) where.sportId = { $in: sportFilters };
    if (ageFilters.length) where.ages = { $in: ageFilters.map(Number) };
    if (genderFilters.length) where.gender = { $in: genderFilters };
    const addFormatFilter = (field: string, values: string[], legacyDefault: string) => {
      if (!values.length) return;
      const explicitValues = values.filter(value => value !== legacyDefault);
      if (values.includes(legacyDefault)) {
        where.$and = [
          ...(where.$and || []),
          { $or: [{ [field]: { $in: values } }, { [field]: { $exists: false } }, { [field]: null }] }
        ];
      } else if (explicitValues.length) {
        where[field] = { $in: explicitValues };
      }
    };
    addFormatFilter('individualEventFormat.category', categoryFilters, 'OTHER');
    addFormatFilter('individualEventFormat.classification', classificationFilters, 'OPEN');
    addFormatFilter('individualEventFormat.stage', roundFilters, 'STRAIGHT_FINAL');
    if (groupFilters.length) {
      where.$and = [...(where.$and || []), { tournamentGroupId: { $in: groupFilters } }];
    }
    if (stageFilters.length) {
      const groupStageIds = new Set(
        (tournament?.tournamentLayout || []).filter(stage => stage.type === 'GROUP').map(stage => String(stage.id))
      );
      const stageConditions: any[] = [];
      if (stageFilters.includes(GROUP_STAGE_FILTER)) {
        const groupIds = (tournament?.customGroups || [])
          .filter(group => groupStageIds.has(String(group.stageId)))
          .map(group => group.id);
        if (groupIds.length) stageConditions.push({ tournamentGroupId: { $in: groupIds } });
      }
      const selectedRounds = stageFilters.filter(value => value !== GROUP_STAGE_FILTER);
      const matches = (this.getCompetitionDefinition()?.matches || []).filter(match =>
        selectedRounds.includes(this.getStructureMatchRound(match))
      );
      const eventIds = matches.map(match => match.eventId).filter(Boolean);
      const structureMatchIds = matches.map(match => match.id).filter(Boolean);
      if (eventIds.length) stageConditions.push({ _id: { $in: eventIds } });
      if (structureMatchIds.length) stageConditions.push({ tournamentStructureMatchId: { $in: structureMatchIds } });
      where.$and = [...(where.$and || []), stageConditions.length ? { $or: stageConditions } : { _id: { $in: [] } }];
    }

    const pitchIds = pitchFilters.filter(id => id !== 'UNASSIGNED');
    const unassigned = pitchFilters.includes('UNASSIGNED');
    if (pitchIds.length && unassigned) {
      where.$and = [...(where.$and || []), { $or: [{ pitchId: { $in: pitchIds } }, { pitchId: { $exists: false } }] }];
    } else if (pitchIds.length) {
      where.pitchId = { $in: pitchIds };
    } else if (unassigned) {
      where.pitchId = { $exists: false };
    }

    const statuses = statusFilters.filter(status => status !== 'NOT_STARTED');
    const includesNotStarted = statusFilters.includes('NOT_STARTED');
    if (statuses.length && includesNotStarted) {
      where.$and = [...(where.$and || []), { $or: [{ status: { $in: statuses } }, { status: { $exists: false } }] }];
    } else if (statuses.length) {
      where.status = { $in: statuses };
    } else if (includesNotStarted) {
      where.status = { $exists: false };
    }
    return where;
  };

  load = async (page: number) => {
    this.setState({ isLoading: true });
    const tournamentId = this.getTournamentId();
    const where = this.getWhere();
    try {
      let result;
      try {
        result = await getTournamentEventsBulkManagement(
          this.props.user,
          tournamentId,
          {
            where,
            skip: (page - 1) * 20,
            limit: 20,
            order: 'startTime ASC'
          },
          new Date().toISOString()
        );
      } catch (bulkError) {
        // Keep Bulk manage compatible with backends deployed before the optimized summary endpoint
        // and with legacy events that the summary projection cannot revive.
        const [items, countResult] = await Promise.all([
          getTournamentEvents(
            this.props.user,
            tournamentId,
            {
              where,
              skip: (page - 1) * 20,
              limit: 20,
              order: 'startTime ASC'
            },
            { summary: true }
          ),
          getTournamentEventsCount(this.props.user, tournamentId, where)
        ]);
        result = {
          items,
          total: countResult.count,
          filterOptions: {
            ages: [...new Set((items || []).flatMap(event => event.ages || []))].sort((a, b) => Number(a) - Number(b)),
            genders: [...new Set((items || []).map(event => event.gender).filter(Boolean))]
          },
          statusCounts: this.state.statusCounts
        };
      }
      this.setState({
        items: result.items || [],
        total: Number(result.total) || 0,
        eventAgeOptions: result.filterOptions?.ages || [],
        eventGenderOptions: result.filterOptions?.genders || [],
        statusCounts: result.statusCounts || { live: 0, upcoming: 0, finished: 0, onHold: 0, cancelled: 0 },
        page,
        isLoading: false
      });
    } catch (error) {
      const details = propz.get(
        error,
        ['response', 'data', 'details', 'text'],
        propz.get(error, ['response', 'data', 'message'], '')
      );
      this.setState({
        isLoading: false,
        saveError: details || 'Events could not be loaded. Please try again.'
      });
    }
  };

  getEventName = (event: SchoolEvent) => {
    const generated = event.generatedNames || {};
    return (
      propz.get(event, ['eventDetails', 'shortDescription'], '') ||
      generated[this.props.user.activeSchoolId] ||
      generated.official ||
      (event.embeddedTeams || [])
        .map(team => team.name)
        .filter(Boolean)
        .join(' vs ') ||
      'Tournament event'
    );
  };

  getGroup = (event: SchoolEvent) =>
    (this.state.tournament?.customGroups || []).find(group => group.id === event.tournamentGroupId);

  getPitch = (event: SchoolEvent) =>
    (this.state.tournament?.pitches || []).find(pitch => pitch.id === event.pitchId)?.name || '';

  getTeams = (event: SchoolEvent) => (event.embeddedTeams || []).slice(0, 2);

  getTeamSchoolName = (event: SchoolEvent, team: any) => {
    const schoolId = String(team.schoolId || '');
    const schools = [
      (event as any).inviterSchool,
      ...((event as any).invitedSchools || []),
      ...(this.state.tournament?.schools || [])
    ].filter(Boolean);
    return schools.find(school => String(school.id || school._id) === schoolId)?.name || '';
  };

  getScores = (event: SchoolEvent) => {
    const teams = this.getTeams(event);
    const scores = propz.get(event, ['results', 'teamScore'], []) as any[];
    return teams.map(team => {
      const score = scores.find(item => String(item.teamId) === String((team as any).id || (team as any)._id));
      const value: any = propz.get(score, ['richScore', 'result'], propz.get(score, ['score'], ''));
      return Number(value) === 0 && value !== '' ? '0' : String(value || '');
    });
  };

  updateDraft = (event: SchoolEvent, patch: Partial<EventDraft>) => {
    this.setState(state => ({
      drafts: {
        ...state.drafts,
        [event.id]: { ...(state.drafts[event.id] || { event }), ...patch, event }
      },
      saveError: ''
    }));
  };

  changeStart = (event: SchoolEvent, value: string) => {
    const nextStart = new Date(value);
    const duration = Math.max(0, new Date(event.endTime).getTime() - new Date(event.startTime).getTime());
    this.updateDraft(event, {
      startTime: nextStart.toISOString(),
      endTime: new Date(nextStart.getTime() + duration).toISOString()
    });
  };

  changeStartPart = (event: SchoolEvent, part: 'date' | 'time', value: string) => {
    if (!value) return;
    const current = new Date(this.state.drafts[event.id]?.startTime || event.startTime);
    if (part === 'date') {
      const [year, month, day] = value.split('-').map(Number);
      current.setFullYear(year, month - 1, day);
    } else {
      const [hours, minutes] = value.split(':').map(Number);
      current.setHours(hours, minutes, 0, 0);
    }
    this.changeStart(event, toDatetimeLocal(current.toISOString()));
  };

  focusEditCell = (rowIndex: number, field: string) => {
    const target = document.querySelector<HTMLInputElement | HTMLSelectElement>(
      `[data-bulk-edit-row="${rowIndex}"][data-bulk-edit-field="${field}"]`
    );
    if (target) {
      target.focus();
      if (target instanceof HTMLInputElement && (target.type === 'text' || target.type === 'number')) target.select();
      return true;
    }
    return false;
  };

  handleEditKeyDown = (
    keyboardEvent: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
    rowIndex: number,
    field: string
  ) => {
    const editableFields = ['date', 'time', 'group', 'pitch', 'description', 'score1', 'score2'];
    if (keyboardEvent.key === 'ArrowDown' || keyboardEvent.key === 'ArrowUp') {
      const nextRowIndex = rowIndex + (keyboardEvent.key === 'ArrowDown' ? 1 : -1);
      if (this.focusEditCell(nextRowIndex, field)) keyboardEvent.preventDefault();
      return;
    }
    if (
      (field === 'date' || field === 'time') &&
      (keyboardEvent.key === 'ArrowRight' || keyboardEvent.key === 'ArrowLeft')
    ) {
      return;
    }
    if (keyboardEvent.key !== 'ArrowRight' && keyboardEvent.key !== 'ArrowLeft') return;

    const direction = keyboardEvent.key === 'ArrowRight' ? 1 : -1;
    let nextRow = rowIndex;
    let nextFieldIndex = editableFields.indexOf(field) + direction;
    while (nextRow >= 0 && nextRow < this.state.items.length) {
      if (nextFieldIndex >= editableFields.length) {
        nextRow += 1;
        nextFieldIndex = 0;
      } else if (nextFieldIndex < 0) {
        nextRow -= 1;
        nextFieldIndex = editableFields.length - 1;
      }
      if (this.focusEditCell(nextRow, editableFields[nextFieldIndex])) {
        keyboardEvent.preventDefault();
        return;
      }
      nextFieldIndex += direction;
    }
  };

  openResultEditor = async (event: SchoolEvent) => {
    if (this.state.isResultLoading) return;
    this.setState({ isResultLoading: true, resultError: '' });
    try {
      const fullEvent = await getSchoolEvent(this.props.user, event.id, { fresh: true });
      this.setState({
        isResultLoading: false,
        resultEvent: { ...fullEvent, tournament: this.state.tournament },
        resultError: ''
      });
    } catch (error) {
      this.setState({
        isResultLoading: false,
        resultError: propz.get(error, ['response', 'data', 'details', 'text'], 'Result details could not be loaded.')
      });
    }
  };

  refreshStructureNotice = async () => {
    try {
      const runtime = await getTournamentCompetitionRuntime(this.props.user, this.getTournamentId());
      const status = runtime.groupReview?.status;
      this.setState({ structureNotice: status === 'READY' || status === 'REQUIRES_DECISION' || status === 'STALE' ? status : undefined });
    } catch {/* Result saving must not fail because the review notice could not be loaded. */}
  };

  onResultSaved = () => {
    this.setState({ resultEvent: undefined, resultError: '' }, () => {
      this.load(this.state.page);
      this.refreshStructureNotice();
    });
  };

  saveAll = async () => {
    const drafts = Object.keys(this.state.drafts).map(eventId => this.state.drafts[eventId]);
    if (!drafts.length || this.state.isSaving) return;
    const incompleteResult = drafts.find(draft => {
      if (draft.score1 === undefined && draft.score2 === undefined) return false;
      const currentScores = this.getScores(draft.event);
      const score1 = draft.score1 !== undefined ? draft.score1 : currentScores[0];
      const score2 = draft.score2 !== undefined ? draft.score2 : currentScores[1];
      return score1 === '' || score2 === '' || !Number.isFinite(Number(score1)) || !Number.isFinite(Number(score2));
    });
    if (incompleteResult) {
      this.setState({ saveError: `Enter both scores for ${this.getEventName(incompleteResult.event)}.` });
      return;
    }
    const updates = drafts.map(draft => {
      const update: any = { eventId: draft.event.id };
      if (draft.shortDescription !== undefined) update.shortDescription = draft.shortDescription.trim();
      if (draft.startTime) {
        update.startTime = draft.startTime;
        update.endTime = draft.endTime;
      }
      if (draft.tournamentGroupId !== undefined) update.tournamentGroupId = draft.tournamentGroupId;
      if (draft.pitchId !== undefined) update.pitchId = draft.pitchId;
      if (draft.score1 !== undefined || draft.score2 !== undefined) {
        const currentScores = this.getScores(draft.event);
        const score1 = draft.score1 !== undefined ? draft.score1 : currentScores[0];
        const score2 = draft.score2 !== undefined ? draft.score2 : currentScores[1];
        const teams = this.getTeams(draft.event);
        if (teams.length === 2 && score1 !== '' && score2 !== '') {
          update.scores = teams.map((team: any, index) => ({
            teamId: team.id || team._id,
            score: Number(index === 0 ? score1 : score2)
          }));
        }
      }
      return update;
    });

    this.setState({ isSaving: true, saveError: '' });
    try {
      const result = await updateTournamentEventsBulk(this.props.user, this.getTournamentId(), updates);
      const status = result?.groupReview?.status;
      this.setState({ drafts: {}, isSaving: false, isInlineMode: false, structureNotice: status === 'READY' || status === 'REQUIRES_DECISION' || status === 'STALE' ? status : undefined }, () => {
        this.load(this.state.page);
      });
    } catch (error) {
      this.setState({
        isSaving: false,
        saveError: propz.get(error, ['response', 'data', 'details', 'text'], 'Changes could not be saved.')
      });
    }
  };

  toggleColumn = (key: string) => {
    if (key === 'fixture') return;
    this.setState(state => {
      const hiddenColumns = toggleValue(state.hiddenColumns, key);
      try {
        localStorage.setItem(storageKey(this.props.user.userId), JSON.stringify(hiddenColumns));
      } catch {}
      return { hiddenColumns };
    });
  };

  openTournamentEvent = (event: SchoolEvent): void => {
    this.props.history.push({
      pathname: '/tournaments/events/event',
      search: `id=${event.id}`,
      state: {
        ...((this.props.location.state || {}) as any),
        prevPath: this.props.location.pathname,
        search: this.props.location.search
      }
    });
  };

  resetColumns = () => {
    const hiddenColumns: string[] = [];
    try {
      localStorage.setItem(storageKey(this.props.user.userId), JSON.stringify(hiddenColumns));
    } catch {}
    this.setState({ hiddenColumns });
  };

  toggleSelected = (event: SchoolEvent) => {
    this.setState(state => {
      const selected = state.selectedIds.includes(event.id);
      const selectedEvents = { ...state.selectedEvents };
      if (selected) delete selectedEvents[event.id];
      else selectedEvents[event.id] = event;
      return {
        selectedIds: selected ? state.selectedIds.filter(id => id !== event.id) : [...state.selectedIds, event.id],
        selectedEvents
      };
    });
  };

  applySelectedIndividualFormat = async () => {
    const { selectedIds, selectedFormatCategory, selectedFormatClassification, selectedFormatStage } = this.state;
    const individualEventFormat: any = {};
    if (selectedFormatCategory) individualEventFormat.category = selectedFormatCategory;
    if (selectedFormatClassification) individualEventFormat.classification = selectedFormatClassification;
    if (selectedFormatStage) individualEventFormat.stage = selectedFormatStage;
    if (!selectedIds.length || !Object.keys(individualEventFormat).length || this.state.isApplyingFormat) return;
    this.setState({ isApplyingFormat: true, saveError: '' });
    try {
      await updateTournamentEventsBulk(this.props.user, this.getTournamentId(), selectedIds.map(eventId => ({ eventId, individualEventFormat })));
      this.setState({
        isApplyingFormat: false,
        selectedIds: [], selectedEvents: {},
        selectedFormatCategory: '', selectedFormatClassification: '', selectedFormatStage: ''
      }, () => this.load(this.state.page));
    } catch (error) {
      this.setState({
        isApplyingFormat: false,
        saveError: propz.get(error, ['response', 'data', 'details', 'text'], 'Event format could not be applied.')
      });
    }
  };

  selectCurrentPage = () => {
    this.setState(state => {
      const selectedEvents = { ...state.selectedEvents };
      state.items.forEach(event => {
        selectedEvents[event.id] = event;
      });
      return {
        selectedIds: Array.from(new Set([...state.selectedIds, ...state.items.map(event => event.id)])),
        selectedEvents
      };
    });
  };

  selectAllFiltered = async () => {
    const tournamentId = this.getTournamentId();
    try {
      const ids = await getTournamentEventIds(this.props.user, tournamentId, this.getWhere());
      const selectedEvents = {...this.state.selectedEvents};
      this.state.items.forEach(event => { if (ids.includes(event.id)) selectedEvents[event.id] = event; });
      this.setState({ selectedIds: ids, selectedEvents });
    } catch {
      this.setState({ saveError: 'Events could not be selected. Please try again.' });
    }
  };

  applyPitch = async () => {
    const { selectedIds, selectedPitchId } = this.state;
    this.setState({ isLoading: true });
    await Promise.all(
      selectedIds.map(eventId =>
        selectedPitchId
          ? assignPitchByTournamentIdAndEventId(
              this.props.user,
              { pitchId: selectedPitchId },
              this.getTournamentId(),
              eventId
            )
          : unassignPitchByTournamentIdAndEventId(this.props.user, this.getTournamentId(), eventId)
      )
    );
    this.setState({ isAssignPitchOpen: false, selectedIds: [], selectedEvents: {}, selectedPitchId: '' });
    this.load(this.state.page);
  };

  deleteSelected = async () => {
    this.setState({ isLoading: true });
    await Promise.all(this.state.selectedIds.map(eventId => deleteEvent(this.props.user, eventId)));
    this.setState({ isDeletingOpen: false, selectedIds: [], selectedEvents: {} });
    this.load(1);
  };

  requestBack = () => {
    if (Object.keys(this.state.drafts).length) this.setState({ isDiscardOpen: true });
    else this.goBack();
  };

  goBack = () => {
    const state = this.props.location.state as any;
    if (state?.prevPath) this.props.history.push({ pathname: state.prevPath, search: state.search || '' });
    else this.props.history.goBack();
  };

  removeAppliedFilter = (key: AppliedFilterKey, value: string) => {
    const draftKey = key.replace('Filters', 'FilterDrafts') as keyof State;
    this.setState(
      state =>
        ({
          [key]: (state[key] as string[]).filter(item => item !== value),
          [draftKey]: (state[draftKey] as string[]).filter(item => item !== value)
        } as Pick<State, AppliedFilterKey>),
      () => this.load(1)
    );
  };

  clearAppliedFilters = () => {
    this.setState(
      {
        sportFilters: [], ageFilters: [], genderFilters: [], groupFilters: [], pitchFilters: [], stageFilters: [],
        statusFilters: [], categoryFilters: [], classificationFilters: [], roundFilters: [],
        sportFilterDrafts: [], ageFilterDrafts: [], genderFilterDrafts: [], groupFilterDrafts: [],
        pitchFilterDrafts: [], stageFilterDrafts: [], statusFilterDrafts: [], categoryFilterDrafts: [],
        classificationFilterDrafts: [], roundFilterDrafts: []
      },
      () => this.load(1)
    );
  };

  render() {
    const { tournament } = this.state;
    if (!tournament) return <Loader />;
    const isTeam = isTeamTournament(tournament) || isMiniTeamTournament(tournament);
    const visible = (key: string) => {
      if (!isTeam && key === 'score') return false;
      return key === 'fixture' || !this.state.hiddenColumns.includes(key);
    };
    const individualOnlyColumns = ['eventCategory', 'classification', 'formatRound'];
    const availableColumns = COLUMNS.filter(column =>
      isTeam ? !individualOnlyColumns.includes(column.key) : column.key !== 'score' && !['stage', 'group'].includes(column.key)
    );
    const visibleCount = availableColumns.filter(column => visible(column.key)).length + 1;
    const activeFilterCount =
      this.state.sportFilters.length +
      this.state.ageFilters.length +
      this.state.genderFilters.length +
      this.state.groupFilters.length +
      this.state.pitchFilters.length +
      this.state.stageFilters.length +
      this.state.statusFilters.length +
      this.state.categoryFilters.length +
      this.state.classificationFilters.length +
      this.state.roundFilters.length;
    const filterChips: Array<{ key: AppliedFilterKey; value: string; label: string }> = [];
    const addFilterChips = (key: AppliedFilterKey, values: string[], options: Array<{ id: string; name: string }>) =>
      values.forEach(value => {
        const option = options.find(item => String(item.id) === value);
        filterChips.push({ key, value, label: option ? option.name : value });
      });
    addFilterChips('sportFilters', this.state.sportFilters, this.state.sports.map(item => ({ id: item.id, name: item.name })));
    addFilterChips('ageFilters', this.state.ageFilters, this.state.ageFilters.map(value => ({ id: value, name: formatTournamentAges([Number(value)]) })));
    addFilterChips('genderFilters', this.state.genderFilters, EVENT_GENDER_OPTIONS);
    addFilterChips('groupFilters', this.state.groupFilters, (tournament.customGroups || []).map(item => ({ id: item.id, name: item.name })));
    addFilterChips('pitchFilters', this.state.pitchFilters, [{ id: 'UNASSIGNED', name: 'Not assigned' }, ...(tournament.pitches || []).map(item => ({ id: item.id, name: item.name }))]);
    addFilterChips('stageFilters', this.state.stageFilters, this.getStageFilterOptions());
    addFilterChips('statusFilters', this.state.statusFilters, EVENT_STATUS_OPTIONS);
    addFilterChips('categoryFilters', this.state.categoryFilters, EVENT_CATEGORY_OPTIONS);
    addFilterChips('classificationFilters', this.state.classificationFilters, EVENT_CLASSIFICATION_OPTIONS);
    addFilterChips('roundFilters', this.state.roundFilters, EVENT_ROUND_OPTIONS);
    const draftCount = Object.keys(this.state.drafts).length;
    const statusSummary = [
      { key: 'live', label: 'Live', count: this.state.statusCounts.live, className: 'is-live' },
      { key: 'upcoming', label: 'Upcoming', count: this.state.statusCounts.upcoming, className: 'is-upcoming' },
      { key: 'finished', label: 'Finished', count: this.state.statusCounts.finished, className: 'is-finished' },
      { key: 'onHold', label: 'On hold', count: this.state.statusCounts.onHold, className: 'is-hold' },
      { key: 'cancelled', label: 'Cancelled', count: this.state.statusCounts.cancelled, className: 'is-cancelled' }
    ].filter(item => item.count > 0);
    const allPageSelected =
      this.state.items.length > 0 && this.state.items.every(event => this.state.selectedIds.includes(event.id));

    return (
      <main className="tournamentBulkEvents">
        <header className="tournamentBulkEvents__pageHeader">
          <button type="button" className="tournamentBulkEvents__back" onClick={this.requestBack}>
            <span aria-hidden="true">←</span>
            Back to {isTeam ? 'fixtures' : 'events'}
          </button>
          <span className="tournamentBulkEvents__headerDivider" aria-hidden="true" />
          <div className="tournamentBulkEvents__heading">
            <span>Bulk manage {isTeam ? 'fixtures' : 'events'}</span>
            <h1>{tournament.name}</h1>
          </div>
          <div className="tournamentBulkEvents__summary">
            <span>
              <strong>{this.state.total}</strong>{' '}
              {this.state.total === 1 ? (isTeam ? 'fixture' : 'event') : isTeam ? 'fixtures' : 'events'}
            </span>
            {this.state.selectedIds.length ? (
              <span className="is-selected">
                <strong>{this.state.selectedIds.length}</strong> selected
              </span>
            ) : null}
            {draftCount ? (
              <span className="is-warning">
                <strong>{draftCount}</strong> {draftCount === 1 ? 'change' : 'changes'} not applied
              </span>
            ) : null}
          </div>
        </header>

        {statusSummary.length ? (
          <div className="tournamentBulkEvents__statusSummary" aria-label="Fixture status summary">
            {statusSummary.map(item => (
              <span key={item.key} className={item.className}>
                <strong>{item.count}</strong> {item.label}
              </span>
            ))}
          </div>
        ) : null}

        <section className="tournamentBulkEvents__panel">
          <header>
            <h2>
              {isTeam ? 'Fixtures' : 'Events'} <b>{this.state.total}</b>
            </h2>
            <button
              type="button"
              className={`tournamentBulkEvents__tool ${activeFilterCount ? 'is-active' : ''}`}
              onClick={() =>
                this.setState({
                  isFiltersOpen: true,
                  sportFilterDrafts: [...this.state.sportFilters],
                  ageFilterDrafts: [...this.state.ageFilters],
                  genderFilterDrafts: [...this.state.genderFilters],
                  groupFilterDrafts: [...this.state.groupFilters],
                  pitchFilterDrafts: [...this.state.pitchFilters],
                  stageFilterDrafts: [...this.state.stageFilters],
                  statusFilterDrafts: [...this.state.statusFilters],
                  categoryFilterDrafts: [...this.state.categoryFilters],
                  classificationFilterDrafts: [...this.state.classificationFilters],
                  roundFilterDrafts: [...this.state.roundFilters]
                })
              }
            >
              <Icon path="M4 6h16M7 12h10M10 18h4" />
              Filters
              {activeFilterCount ? <b>{activeFilterCount}</b> : null}
            </button>
            <button
              type="button"
              className="tournamentBulkEvents__tool"
              onClick={() => this.setState({ isColumnsOpen: true })}
            >
              <Icon path="M5 4h14v16H5zM9 4v16M15 4v16" />
              Columns
              <b>{availableColumns.filter(column => visible(column.key)).length}</b>
            </button>
            <button
              type="button"
              className={`tournamentBulkEvents__tool ${this.state.isInlineMode ? 'is-active' : ''}`}
              onClick={() => this.setState(state => ({ isInlineMode: !state.isInlineMode }))}
            >
              <Icon path="M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4" />
              {this.state.isInlineMode ? 'Exit inline mode' : 'Inline edit'}
            </button>
            <div className="tournamentBulkEvents__headerEnd">
              {draftCount ? <span>{draftCount} changed</span> : null}
              <button
                type="button"
                className="is-primary"
                disabled={!draftCount || this.state.isSaving}
                onClick={this.saveAll}
              >
                {this.state.isSaving ? 'Saving…' : 'Apply all changes'}
              </button>
            </div>
          </header>

          {filterChips.length ? (
            <div className="tournamentBulkEvents__activeFilters" aria-label="Active filters">
              <span>Filtered by</span>
              {filterChips.map(chip => (
                <button type="button" key={`${chip.key}-${chip.value}`} onClick={() => this.removeAppliedFilter(chip.key, chip.value)}>
                  {chip.label}<i aria-hidden="true">×</i>
                </button>
              ))}
              <button type="button" className="is-clear" onClick={this.clearAppliedFilters}>Clear all</button>
            </div>
          ) : null}

          {this.state.saveError ? <div className="tournamentBulkEvents__error">{this.state.saveError}</div> : null}
          {this.state.structureNotice ? <div className={`tournamentBulkEvents__structureNotice is-${this.state.structureNotice.toLowerCase()}`} role="alert"><span><strong>{this.state.structureNotice==='REQUIRES_DECISION'?'Group stage complete · decision required':this.state.structureNotice==='STALE'?'Group qualification needs reviewing again':'Group stage complete · qualification ready'}</strong><small>{this.state.structureNotice==='REQUIRES_DECISION'?'Some teams remain level after the automatic criteria. Review and order them before creating the next fixtures.':this.state.structureNotice==='STALE'?'A group result changed after qualification was confirmed. Check the standings before progression continues.':'Review the final standings and confirm the teams progressing to the next stage.'}</small></span><button type="button" onClick={()=>this.props.history.push({pathname:'/tournaments/manage/competition/structure',search:`tournament=${this.getTournamentId()}`})}>Review tournament structure</button></div> : null}
          {this.state.selectedIds.length ? (
            <div className="tournamentBulkEvents__selection">
              <strong>Selected {this.state.selectedIds.length}</strong>
              {!isTeam && <div className="tournamentBulkEvents__formatActions">
                <select aria-label="Event category" value={this.state.selectedFormatCategory} onChange={event => this.setState({ selectedFormatCategory: event.target.value })}>
                  <option value="">Category…</option><option value="TRACK">Track</option><option value="FIELD">Field</option><option value="POOL">Pool</option><option value="COURSE">Course</option><option value="COURT">Court</option><option value="OTHER">Other</option>
                </select>
                <select aria-label="Classification" value={this.state.selectedFormatClassification} onChange={event => this.setState({ selectedFormatClassification: event.target.value })}>
                  <option value="">Classification…</option><option value="OPEN">Open</option><option value="PARA">Para</option>
                </select>
                <select aria-label="Competition round" value={this.state.selectedFormatStage} onChange={event => this.setState({ selectedFormatStage: event.target.value })}>
                  <option value="">Round…</option><option value="STRAIGHT_FINAL">Straight final</option><option value="HEAT">Heat</option><option value="QUALIFIER">Qualifier</option><option value="SEMI_FINAL">Semi-final</option><option value="FINAL">Final</option>
                </select>
                <button type="button" className="is-primary" disabled={this.state.isApplyingFormat || !(this.state.selectedFormatCategory || this.state.selectedFormatClassification || this.state.selectedFormatStage)} onClick={this.applySelectedIndividualFormat}>{this.state.isApplyingFormat ? 'Applying…' : 'Apply format'}</button>
              </div>}
              <button type="button" onClick={() => this.setState({ isAssignPitchOpen: true })}>
                Assign pitch
              </button>
              <button type="button" className="is-danger" onClick={() => this.setState({ isDeletingOpen: true })}>
                Delete
              </button>
              <button type="button" onClick={() => this.setState({ selectedIds: [], selectedEvents: {} })}>
                Clear
              </button>
            </div>
          ) : null}

          <div className="tournamentBulkEvents__tableWrap">
            <table>
              <thead>
                <tr>
                  <th>
                    <SelectAllCheckbox
                      compact
                      isChecked={allPageSelected}
                      totalCount={this.state.total}
                      currentPage={this.state.page}
                      pageItemsCount={this.state.items.length}
                      selectedItems={this.state.selectedIds}
                      onSelectAllOnPageClick={this.selectCurrentPage}
                      onSelectAllClick={this.selectAllFiltered}
                      onUnselectAllClick={() => this.setState({ selectedIds: [], selectedEvents: {} })}
                    />
                  </th>
                  {availableColumns.map(column =>
                    visible(column.key) ? <th key={column.key}>{column.label}</th> : null
                  )}
                </tr>
              </thead>
              <tbody>
                {this.state.isLoading ? (
                  <tr>
                    <td colSpan={visibleCount}>Loading {isTeam ? 'fixtures' : 'events'}…</td>
                  </tr>
                ) : null}
                {!this.state.isLoading &&
                  this.state.items.map((event, rowIndex) => {
                    const draft = this.state.drafts[event.id];
                    const effectiveGroupId = draft?.tournamentGroupId ?? event.tournamentGroupId;
                    const effectivePitchId = draft?.pitchId === undefined ? event.pitchId || '' : draft.pitchId || '';
                    const group = (tournament.customGroups || []).find(item => item.id === effectiveGroupId);
                    const stage = (tournament.tournamentLayout || []).find(item => item.id === group?.stageId);
                    const knockoutRound = this.getEventKnockoutRound(event);
                    const scores = this.getScores(event);
                    const description =
                      draft?.shortDescription !== undefined
                        ? draft.shortDescription
                        : propz.get(event, ['eventDetails', 'shortDescription'], '');
                    return (
                      <tr key={event.id} className={draft ? 'is-modified' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={this.state.selectedIds.includes(event.id)}
                            onChange={() => this.toggleSelected(event)}
                          />
                        </td>
                        {visible('number') ? <td>{event.quantumEventNumber || '—'}</td> : null}
                        {visible('sport') ? <td>{event.sport?.name || '—'}</td> : null}
                        {visible('age') ? <td>{formatTournamentAges(event.ages) || '—'}</td> : null}
                        {visible('gender') ? (
                          <td>{TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[event.gender] || event.gender || '—'}</td>
                        ) : null}
                        {!isTeam && visible('eventCategory') ? <td>{event.individualEventFormat?.category || 'Other'}</td> : null}
                        {!isTeam && visible('classification') ? <td>{event.individualEventFormat?.classification === 'PARA' ? 'Para' : 'Open'}</td> : null}
                        {!isTeam && visible('formatRound') ? <td>{String(event.individualEventFormat?.stage || 'STRAIGHT_FINAL').replace(/_/g, ' ').toLowerCase().replace(/(^|\s)\S/g, value => value.toUpperCase())}</td> : null}
                        {visible('date') ? (
                          <td>
                            {this.state.isInlineMode ? (
                              <input
                                className="is-date"
                                type="date"
                                value={toDatetimeLocal(draft?.startTime || event.startTime).slice(0, 10)}
                                data-bulk-edit-row={rowIndex}
                                data-bulk-edit-field="date"
                                onKeyDown={e => this.handleEditKeyDown(e, rowIndex, 'date')}
                                onChange={e => this.changeStartPart(event, 'date', e.target.value)}
                              />
                            ) : (
                              new Date(event.startTime).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })
                            )}
                          </td>
                        ) : null}
                        {visible('startTime') ? (
                          <td>
                            {this.state.isInlineMode ? (
                              <input
                                className="is-time"
                                type="time"
                                step="300"
                                value={toDatetimeLocal(draft?.startTime || event.startTime).slice(11, 16)}
                                data-bulk-edit-row={rowIndex}
                                data-bulk-edit-field="time"
                                onKeyDown={e => this.handleEditKeyDown(e, rowIndex, 'time')}
                                onChange={e => this.changeStartPart(event, 'time', e.target.value)}
                              />
                            ) : (
                              new Date(event.startTime).toLocaleTimeString('en-GB', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            )}
                          </td>
                        ) : null}
                        {isTeam && visible('stage') ? (
                          <td>{knockoutRound || (stage?.type === 'GROUP' ? 'Group stage' : stage?.name || '—')}</td>
                        ) : null}
                        {isTeam && visible('group') ? (
                          <td>
                            {this.state.isInlineMode ? (
                              <select
                                value={effectiveGroupId || ''}
                                data-bulk-edit-row={rowIndex}
                                data-bulk-edit-field="group"
                                onKeyDown={e => this.handleEditKeyDown(e, rowIndex, 'group')}
                                onChange={e => this.updateDraft(event, { tournamentGroupId: e.target.value })}
                              >
                                <option value="">Select group</option>
                                {(tournament.customGroups || []).map(item => (
                                  <option key={item.id} value={item.id}>
                                    {item.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              group?.name || '—'
                            )}
                          </td>
                        ) : null}
                        {visible('pitch') ? (
                          <td>
                            {this.state.isInlineMode ? (
                              <select
                                value={effectivePitchId}
                                data-bulk-edit-row={rowIndex}
                                data-bulk-edit-field="pitch"
                                onKeyDown={e => this.handleEditKeyDown(e, rowIndex, 'pitch')}
                                onChange={e => this.updateDraft(event, { pitchId: e.target.value || null })}
                              >
                                <option value="">Not assigned</option>
                                {(tournament.pitches || []).map(item => (
                                  <option key={item.id} value={item.id}>
                                    {item.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              (tournament.pitches || []).find(item => item.id === effectivePitchId)?.name || '—'
                            )}
                          </td>
                        ) : null}
                        <td className="is-fixture">
                          <button type="button" className="tournamentBulkEvents__eventLink" onClick={() => this.openTournamentEvent(event)}>
                            {this.getEventName(event)}
                          </button>
                          <div className="tournamentBulkEvents__teams">
                            {this.getTeams(event).map((team: any, teamIndex) => (
                              <span key={team.id || team._id}>
                                <b>{this.getTeamSchoolName(event, team) || 'School'}</b>
                                {teamIndex === 0 ? <i>vs</i> : null}
                              </span>
                            ))}
                          </div>
                        </td>
                        {visible('description') ? (
                          <td>
                            {this.state.isInlineMode ? (
                              <input
                                type="text"
                                maxLength={160}
                                value={description}
                                placeholder="Add short description"
                                data-bulk-edit-row={rowIndex}
                                data-bulk-edit-field="description"
                                onKeyDown={e => this.handleEditKeyDown(e, rowIndex, 'description')}
                                onChange={e => this.updateDraft(event, { shortDescription: e.target.value })}
                              />
                            ) : (
                              description || '—'
                            )}
                          </td>
                        ) : null}
                        {visible('score') ? (
                          <td className="tournamentBulkEvents__resultCell">
                            {this.state.isInlineMode&&isTeam&&this.getTeams(event).length===2?<div className="tournamentBulkEvents__score"><input type="number" min="0" step="1" value={draft?.score1!==undefined?draft.score1:scores[0]} aria-label="First team score" data-bulk-edit-row={rowIndex} data-bulk-edit-field="score1" onKeyDown={e=>this.handleEditKeyDown(e,rowIndex,'score1')} onChange={e=>this.updateDraft(event,{score1:e.target.value})}/><i>–</i><input type="number" min="0" step="1" value={draft?.score2!==undefined?draft.score2:scores[1]} aria-label="Second team score" data-bulk-edit-row={rowIndex} data-bulk-edit-field="score2" onKeyDown={e=>this.handleEditKeyDown(e,rowIndex,'score2')} onChange={e=>this.updateDraft(event,{score2:e.target.value})}/></div>:<span>{scores.length === 2 && (scores[0] !== '' || scores[1] !== '') ? `${scores[0]} : ${scores[1]}` : '—'}</span>}
                            {isTeam && this.getTeams(event).length === 2 ? (
                              <button
                                type="button"
                                className="tournamentBulkEvents__resultEditor"
                                disabled={this.state.isResultLoading}
                                onClick={() => this.openResultEditor(event)}
                                aria-label={scores.length === 2 && (scores[0] !== '' || scores[1] !== '') ? 'Update full results' : 'Add full results'}
                                title={scores.length === 2 && (scores[0] !== '' || scores[1] !== '') ? 'Update full results' : 'Add full results'}
                              >
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 19.5V20h.5L16.9 7.6l-4.5-4.5L0 15.5V20h4zm10.3-14.3 1.5-1.5a1.5 1.5 0 0 1 2.1 0l2.4 2.4a1.5 1.5 0 0 1 0 2.1l-1.5 1.5-4.5-4.5z"/></svg>
                              </button>
                            ) : null}
                          </td>
                        ) : null}
                        {visible('status') ? (
                          <td>
                            <span className="tournamentBulkEvents__status">
                              {getEventStatus(event) || 'Not started'}
                            </span>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                {!this.state.isLoading && !this.state.items.length ? (
                  <tr>
                    <td colSpan={visibleCount}>No {isTeam ? 'fixtures' : 'events'} match the selected filters.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <div className="tournamentBulkEvents__pagination">
            <Pagination2
              modern
              currentPage={this.state.page}
              total={this.state.total}
              limit={20}
              setCurrentPageParams={this.load}
            />
          </div>
        </section>

        {this.state.isFiltersOpen ? (
          <div className="tournamentBulkEvents__backdrop" onMouseDown={() => this.setState({ isFiltersOpen: false })}>
            <form
              className="tournamentBulkEvents__filterPanel"
              onMouseDown={e => e.stopPropagation()}
              onSubmit={e => {
                e.preventDefault();
                this.setState(
                  {
                    sportFilters: [...this.state.sportFilterDrafts],
                    ageFilters: [...this.state.ageFilterDrafts],
                    genderFilters: [...this.state.genderFilterDrafts],
                    groupFilters: [...this.state.groupFilterDrafts],
                    pitchFilters: [...this.state.pitchFilterDrafts],
                    stageFilters: [...this.state.stageFilterDrafts],
                    statusFilters: [...this.state.statusFilterDrafts],
                    categoryFilters: [...this.state.categoryFilterDrafts],
                    classificationFilters: [...this.state.classificationFilterDrafts],
                    roundFilters: [...this.state.roundFilterDrafts],
                    isFiltersOpen: false
                  },
                  () => this.load(1)
                );
              }}
            >
              <header>
                <div>
                  <h2>{isTeam ? 'Fixture' : 'Event'} filters</h2>
                  <p>Select one or more values in each section.</p>
                </div>
                <button
                  type="button"
                  onClick={() => this.setState({ isFiltersOpen: false })}
                  aria-label="Close filters"
                >
                  ×
                </button>
              </header>
              <div>
                <section>
                  <SearchMultiSelectFilter
                    label="Sports"
                    options={this.state.sports.map(sport => ({ value: sport.id, label: sport.name }))}
                    selected={this.state.sportFilterDrafts}
                    onToggle={value => this.setState(state => ({ sportFilterDrafts: toggleValue(state.sportFilterDrafts, value) }))}
                    placeholder="Search and select sports…"
                    emptyText="No matching sports"
                  />
                </section>
                <section>
                  <h3>Ages</h3>
                  <div>
                    {(this.state.eventAgeOptions.length ? this.state.eventAgeOptions : tournament.ages || []).map(age => {
                      const ageId = String(age);
                      return (
                        <button
                          type="button"
                          key={ageId}
                          className={this.state.ageFilterDrafts.includes(ageId) ? 'is-selected' : ''}
                          onClick={() =>
                            this.setState(state => ({
                              ageFilterDrafts: toggleValue(state.ageFilterDrafts, ageId)
                            }))
                          }
                        >
                          {formatTournamentAges([age])}
                        </button>
                      );
                    })}
                  </div>
                </section>
                <section>
                  <h3>Gender</h3>
                  <div>
                    {EVENT_GENDER_OPTIONS.filter(
                      gender =>
                        !this.state.eventGenderOptions.length || this.state.eventGenderOptions.includes(gender.id)
                    ).map(gender => (
                      <button
                        type="button"
                        key={gender.id}
                        className={this.state.genderFilterDrafts.includes(gender.id) ? 'is-selected' : ''}
                        onClick={() =>
                          this.setState(state => ({
                            genderFilterDrafts: toggleValue(state.genderFilterDrafts, gender.id)
                          }))
                        }
                      >
                        {gender.name}
                      </button>
                    ))}
                  </div>
                </section>
                {!isTeam ? (
                  <React.Fragment>
                    {[
                      { title: 'Categories', values: EVENT_CATEGORY_OPTIONS, field: 'categoryFilterDrafts' as const },
                      { title: 'Classifications', values: EVENT_CLASSIFICATION_OPTIONS, field: 'classificationFilterDrafts' as const },
                      { title: 'Rounds', values: EVENT_ROUND_OPTIONS, field: 'roundFilterDrafts' as const }
                    ].map(section => (
                      <section key={section.title}>
                        <h3>{section.title}</h3>
                        <div>
                          {section.values.map(item => (
                            <button type="button" key={item.id} className={this.state[section.field].includes(item.id) ? 'is-selected' : ''} onClick={() => this.setState(state => ({ [section.field]: toggleValue(state[section.field], item.id) } as any))}>
                              {item.name}
                            </button>
                          ))}
                        </div>
                      </section>
                    ))}
                  </React.Fragment>
                ) : null}
                {[
                  ...((tournament.customGroups || []).length ? [{ title: 'Groups & competitions', values: tournament.customGroups || [], field: 'groupFilterDrafts' as const }] : []),
                  ...((tournament.pitches || []).length ? [{
                    title: 'Pitches',
                    values: [{ id: 'UNASSIGNED', name: 'Not assigned' }, ...(tournament.pitches || [])],
                    field: 'pitchFilterDrafts' as const
                  }] : []),
                  ...(isTeam && this.getStageFilterOptions().length ? [{ title: 'Stages', values: this.getStageFilterOptions(), field: 'stageFilterDrafts' as const }] : []),
                  { title: 'Statuses', values: EVENT_STATUS_OPTIONS, field: 'statusFilterDrafts' as const }
                ].map(section => (
                  <section key={section.title}>
                    <h3>{section.title}</h3>
                    <div>
                      {section.values.map((item: any) => (
                        <button
                          type="button"
                          key={item.id}
                          className={this.state[section.field].includes(item.id) ? 'is-selected' : ''}
                          onClick={() =>
                            this.setState(
                              state => ({ [section.field]: toggleValue(state[section.field], item.id) } as any)
                            )
                          }
                        >
                          {item.name || item.type}
                        </button>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
              <footer>
                <button
                  type="button"
                  onClick={() =>
                    this.setState({
                      sportFilterDrafts: [],
                      ageFilterDrafts: [],
                      genderFilterDrafts: [],
                      groupFilterDrafts: [],
                      pitchFilterDrafts: [],
                      stageFilterDrafts: [],
                      statusFilterDrafts: [],
                      categoryFilterDrafts: [],
                      classificationFilterDrafts: [],
                      roundFilterDrafts: []
                    })
                  }
                >
                  Clear all
                </button>
                <button type="button" onClick={() => this.setState({ isFiltersOpen: false })}>
                  Cancel
                </button>
                <button type="submit" className="is-primary">
                  Apply
                </button>
              </footer>
            </form>
          </div>
        ) : null}

        {this.state.isColumnsOpen ? (
          <div className="tournamentBulkEvents__backdrop" onMouseDown={() => this.setState({ isColumnsOpen: false })}>
            <div className="tournamentBulkEvents__columns" onMouseDown={e => e.stopPropagation()}>
              <header>
                <h2>Table columns</h2>
                <button type="button" className="utmColumnsReset" onClick={this.resetColumns}>
                  Reset
                </button>
                <button type="button" onClick={() => this.setState({ isColumnsOpen: false })}>
                  ×
                </button>
              </header>
              <div>
                {availableColumns.map(column => (
                  <label key={column.key}>
                    <input
                      type="checkbox"
                      checked={visible(column.key)}
                      disabled={column.required}
                      onChange={() => this.toggleColumn(column.key)}
                    />
                    <span>{column.label}</span>
                    {column.required ? <small>Always shown</small> : null}
                  </label>
                ))}
              </div>
              <footer>
                <button type="button" onClick={() => this.setState({ isColumnsOpen: false })}>
                  Done
                </button>
              </footer>
            </div>
          </div>
        ) : null}

        {this.state.resultError ? (
          <div className="tournamentBulkEvents__resultError" role="alert">
            {this.state.resultError}
          </div>
        ) : null}

        <SimpleModal
          isOpen={Boolean(this.state.resultEvent)}
          title={
            this.state.resultEvent &&
            (this.state.resultEvent.matchResult || this.getScores(this.state.resultEvent).some(score => score !== ''))
              ? 'Update results'
              : 'Add results'
          }
          customClass="utmRichResultModal"
          dialogStyle={{ maxWidth: 720 }}
          showFooter={false}
          isBodyBold={false}
          onCloseClick={() => this.setState({ resultEvent: undefined, resultError: '' })}
          onBackdropClick={() => this.setState({ resultEvent: undefined, resultError: '' })}
        >
          {this.state.resultEvent ? (
            <RichMatchResultEditor
              key={`${this.state.resultEvent.id}:${this.state.resultEvent.updatedAt || ''}`}
              user={this.props.user}
              event={this.state.resultEvent}
              onSaved={this.onResultSaved}
            />
          ) : null}
        </SimpleModal>

        <SimpleModal
          isOpen={this.state.isAssignPitchOpen}
          title="Assign pitch"
          showFooter={false}
          dialogStyle={{ maxWidth: 420 }}
          onCloseClick={() => this.setState({ isAssignPitchOpen: false })}
        >
          <div className="tournamentBulkEvents__pitchModal">
            <p>
              {this.state.selectedIds.length} selected {isTeam ? 'fixtures' : 'events'}
            </p>
            <select
              value={this.state.selectedPitchId}
              onChange={e => this.setState({ selectedPitchId: e.target.value })}
            >
              <option value="">Not assigned</option>
              {(tournament.pitches || []).map(pitch => (
                <option key={pitch.id} value={pitch.id}>
                  {pitch.name}
                </option>
              ))}
            </select>
            <footer>
              <button type="button" onClick={() => this.setState({ isAssignPitchOpen: false })}>
                Cancel
              </button>
              <button type="button" className="is-primary" onClick={this.applyPitch}>
                Apply
              </button>
            </footer>
          </div>
        </SimpleModal>
        <SimpleModal
          isOpen={this.state.isDeletingOpen}
          title={`Delete selected ${isTeam ? 'fixtures' : 'events'}?`}
          showFooter={false}
          customClass="tournamentBulkEvents__deleteDialog"
          dialogStyle={{ maxWidth: 410 }}
          onCloseClick={() => this.setState({ isDeletingOpen: false })}
        >
          <div className="tournamentBulkEvents__deleteModal">
            <div className="tournamentBulkEvents__deleteNotice">
              <span aria-hidden="true">!</span>
              <div>
                <strong>This cannot be undone</strong>
                <p>
                  {this.state.selectedIds.length} selected {isTeam ? 'fixtures' : 'events'} will be permanently
                  deleted.
                </p>
              </div>
            </div>
            <footer>
              <button type="button" onClick={() => this.setState({ isDeletingOpen: false })}>
                Keep fixtures
              </button>
              <button type="button" className="is-danger" disabled={this.state.isLoading} onClick={this.deleteSelected}>
                {this.state.isLoading ? 'Deleting…' : `Delete ${this.state.selectedIds.length}`}
              </button>
            </footer>
          </div>
        </SimpleModal>
        <UnsavedChangesModal
          isOpen={this.state.isDiscardOpen}
          message={`You have unapplied ${
            isTeam ? 'fixture' : 'event'
          } changes. If you leave now, those changes will be lost.`}
          onKeepEditing={() => this.setState({ isDiscardOpen: false })}
          onDiscard={() => this.setState({ drafts: {}, isDiscardOpen: false }, this.goBack)}
        />
      </main>
    );
  }
}
